import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./components/AuthContext";
import ParticlesBackground from "./components/ParticlesBackground";
import AppNavbar from "./components/Navbar";
import Education from "./components/Education";
import Models from "./components/Models";
import Momentum from "./components/Momentum";
import Volatility from "./components/Volatility";
import SectorResearch from "./components/SectorResearch";
import SignalsDashboard from "./components/SignalsDashboard";
import Technicals from "./components/Technicals";
import Seasonality from "./components/Seasonality";
import Wolf from "./components/Wolf";
import 'bootstrap/dist/css/bootstrap.min.css';
import './components/Education.css';
import "./App.css";

const DISCLAIMER_TEXT = `The content on this website is for **educational and informational purposes only**.
It **does not** constitute financial, investment, or trading advice.
Machina Quanta is not responsible for any financial losses that may occur from reliance on the content presented.
Users should conduct their **own research** and consult a **licensed financial professional** before making any investment decisions.`;

const Home = () => (
    <div className="App">
        <header className="App-header">
            <AppNavbar />
            <ParticlesBackground />
            <div className="header-content">
                <h1>Predict Trends | Enhance Decisions</h1>
            </div>
        </header>

        <main>
            <section className="about-section">
                <h2>About the Pullback Indicator</h2>
                <p className="intro-text">
                    The pullback indicator is designed to provide insights into future market stability. 
                    It helps identify market drawdowns before they occur, giving you an opportunity to protect your assets.
                    This tool uses research and techniques from machine learning, statistics, and signal processing.
                </p>
                
                <div className="how-it-works">
                    <h3>How the Pullback Indicator Works</h3>
                    <div className="explanation-grid">
                        <div className="explanation-item">
                            <strong>High Entropy (Normal Market):</strong> Uniform distribution of market participant views indicates stable conditions.
                        </div>
                        <div className="explanation-item">
                            <strong>Low Entropy (Warning Signal):</strong> Concentrated participant views signal potential drawdowns and increased volatility.
                        </div>
                    </div>
                </div>
                <div className="cta-section">
                    <a href="/models" className="cta-button">
                        <span className="cta-icon">📈</span>
                        <span className="cta-text">
                            <strong>Access Live Market Intelligence</strong>
                            <small>Real-time pullback indicators for SPX, NDX, RUT</small>
                        </span>
                        <span className="cta-arrow">→</span>
                    </a>
                    <p className="cta-subtitle">Join subscribers already using our predictive signals</p>
                </div>
            </section>

            {/* Chart Preview Section */}
            <section className="chart-preview-section">
                <h2>See What You'll Get Access To</h2>
                <p>Interactive charts showing how the pullback indicator predicted major market events</p>
                <div className="chart-preview-container">
                    <img 
                        src="/images/Entropy_Covid.png"
                        alt="Pullback Indicator Performance During Major Market Events" 
                        className="chart-preview-image"
                    />
                    <div className="preview-overlay">
                        <div className="preview-message">
                            <h3>🔒 Get Live Market Intelligence</h3>
                            <p>Access real-time pullback indicators for SPX, NDX, and RUT with interactive charts updated every 5 minutes</p>
                            <a href="/models" className="preview-cta-button">
                                Access Live Charts →
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Embed Video Below Interactive Chart */}
            <section className="video-section">
                <h2>How to Use the Pullback Indicator</h2>
                <video controls className="explainer-video">
                    <source src="/entropy-explainer.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            </section>

            {/* Static COVID Chart Section */}
            <section className="chart-info">
                <img
                    src="/images/Entropy_Covid.png"
                    alt="Chart Guide"
                    className="chart-guide-image"
                />
                <div className="chart-description">
                    <h2>What to Look For</h2>
                    <p>
                        The chart above shows the relationship between the pullback indicator and the SPX close prices during some relevant time periods, including Volmageddon (Feb. 5, 2018) and Covid (Q1 2020).
                        Rapid and/or sustained decreases in this indicator without 'recovery' can signal upcoming pullbacks near-term. The sharper and more extended the decline
                        the larger the ensuing pullback in the S&P 500 index.
                    </p>
                    <p>
                        Key things to look for:
                    </p>
                    <ul>
                        <li>
                            <strong>Decreases in indicator:</strong> Can be a leading signal of upcoming market instability.
                        </li>
                        <li>
                            <strong>Increases in indicator:</strong> Can indicate market stabilization.
                        </li>
                        <li>
                            Observe the relationship between the pullback indicator leading SPX prices by several weeks or months for portfolio protection.
                        </li>
                    </ul>
                </div>
            </section>
        </main>

        {/* Testimonials Section */}
        <section className="testimonials-section">
            <h2>What Our Subscribers Say</h2>
            <div className="testimonials-container">
                <div className="testimonial-card">
                    <div className="testimonial-content">
                        <p>Thanks for saving me a bunch of money this week!</p>
                    </div>
                    <div className="testimonial-author">
                        <strong>Subscriber</strong>
                        <span>Substack Community</span>
                    </div>
                </div>
                <div className="testimonial-card">
                    <div className="testimonial-content">
                        <p>You a badass Also you have good intentions and I like to support people that put in the effort and skillets to back up their intentions. Your indicators match my trading style</p>
                    </div>
                    <div className="testimonial-author">
                        <strong>Trader</strong>
                        <span>Substack Community</span>
                    </div>
                </div>
                <div className="testimonial-card">
                    <div className="testimonial-content">
                        <p>I've found your posts and chat conversations incredibly helpful on my options trading journey, which I started just recently. I truly appreciate you sharing your invaluable experience!</p>
                    </div>
                    <div className="testimonial-author">
                        <strong>Options Trader</strong>
                        <span>Substack Community</span>
                    </div>
                </div>
                <div className="testimonial-card">
                    <div className="testimonial-content">
                        <p>Excellent analysis, well written</p>
                    </div>
                    <div className="testimonial-author">
                        <strong>Reader</strong>
                        <span>Substack Community</span>
                    </div>
                </div>
            </div>
        </section>

        <footer className="App-footer">
            <div className="disclaimer-container">
                <p className="legal-disclaimer">
                    <strong>⚠️ Important Disclaimer: </strong> {DISCLAIMER_TEXT}
                </p>
            </div>
            <p>&copy; 2023-{new Date().getFullYear()} machinaquanta.com. All rights reserved.</p>
        </footer>
    </div>
);

const App = () => {
    return (
        <AuthProvider>
            <Router>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/models" element={<Models />} />
                    <Route path="/momentum" element={<Momentum />} />
                    <Route path="/volatility" element={<Volatility />} />
                    <Route path="/seasonality" element={<Seasonality />} />
                    <Route path="/sector-research" element={<SectorResearch />} />
                    <Route path="/signals" element={<SignalsDashboard />} />
                    <Route path="/technicals" element={<Technicals />} />
                    <Route path="/education" element={<Education />} />
                    <Route path="/wolf" element={<Wolf />} />
                </Routes>
            </Router>
        </AuthProvider>
    );
};

export default App;
